import { QueryOptions, useMutation, useQuery, useQueryClient } from "react-query";
import { useFetchContext } from "./FetchContext";
import { urls } from "./urls";

export const useTestingStats = (
  facility: string,
  options: QueryOptions = {}
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: "test_stats",
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.procedureResults.procedureStats(facility)
      );
      return data;
    },
    ...options,
  });
};

export const useNapaStats = (
  options: QueryOptions = {}
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: "napa_stats",
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.procedureResults.procedureStats("NAPA")
      );
      return data;
    },
    ...options,
  });
};

export const useDCLStats = (
  options: QueryOptions = {}
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: "dcl_stats",
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.procedureResults.procedureStats("DCL")
      );
      return data;
    },
    ...options,
  });
};

export const useLabels = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: "labels",
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.labels.all);
      return data;
    },
    ...options,
  });
};

export const useNote = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["flagNote", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.notes.noteDetails(id));
      return data;
    },
    enabled: id !== undefined && id !== null,
    notifyOnChangeProps: ["data", "error"],
    ...options,
  });
};

export const useComments = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["comments", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.notes.comments(id));
      return data;
    },
    enabled: id !== undefined && id !== null,
    ...options,
  });
};

export const useUsers = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: "activeUsers",
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.users.active);
      return data.results;
    },

    ...options,
  });
};

export const useAttachments = (
  attachmentArray: any[],
  options: QueryOptions = {}
) => {
  const { authAxiosFromUrl } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["attachments", attachmentArray],
    queryFn: async (): Promise<any> => {
      let tempFiles: any[] = [];
      for (const attachment of attachmentArray) {
        if (
          attachment.name.toLowerCase().includes(".png") ||
          attachment.name.toLowerCase().includes(".jpg")
        ) {
          const { data } = await authAxiosFromUrl.get(attachment.file, {
            responseType: "arraybuffer",
          });
          const blob = new Blob([data], { type: "image/jpeg" });
          tempFiles.push({
            ...attachment,
            image: URL.createObjectURL(blob),
          });
        } else {
          tempFiles.push({ ...attachment, image: undefined });
        }
      }
      tempFiles.sort(function (a, b) {
        const temp1 = new Date(a.uploaded_datetime);
        const temp2 = new Date(b.uploaded_datetime);
        return temp1 > temp2 ? -1 : 1;
      });

      return tempFiles;
    },
    enabled:
      attachmentArray !== undefined &&
      attachmentArray !== null &&
      attachmentArray.length > 0,
    ...options,
  });
};

export const useUnitById = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["unit_by_id", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.units.unitDetails(id));
      return data;
    },

    ...options,
  });
};

export const useProjectById = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["project_by_id", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.projects.projectDetails(id));
      return data;
    },
    ...options,
  });
};

export const useProcedureById = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["procedure_by_id", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.procedureResults.procedureDetails(id)
      );
      return data;
    },

    ...options,
  });
};

export const useEmailNote = (id: number) => {
  const { authAxios } = useFetchContext();

  const emailNote = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.notes.emailNote(id));
    return data;
  };
  return useQuery(["email_note", id], emailNote, {
    enabled: false,
  });
};

export const useFileByID = (id: string) => {
  const { authAxios } = useFetchContext();

  const fileByID = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.files.azureFileDetails(id));
    return data;
  };
  return useQuery(["file", id], fileByID);
};

export const CheckListAddNotes = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportWriterAgenda.Add_Check_list, {
    ...postParams,
  });
  return data;
};


export const useCheckListAddNotes = () => {
  return useMutation(CheckListAddNotes);
};


export const UpdateCheckListAddNotes = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.put(urls.reportWriterAgenda.Add_Check_list, {
    ...postParams,
  });
  return data;
};


export const useUpdateCheckListAddNotes = () => {
  return useMutation(UpdateCheckListAddNotes);
};

export const useWriterHistory = (enabled = true) => {
  const { authAxios } = useFetchContext();

  const getWriterHistory = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportWriterAgenda.writerHistory);
    return data.results;
  };

  return useQuery("writer_history", getWriterHistory, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useCheckListComments = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["checklist-comments", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.reportWriterAgenda.Checklist_comments(id));
      return data;
    },
    enabled: id !== undefined && id !== null,
    ...options,
  });
};

export const writerUpdate = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportWriterAgenda.reportWriter, {
    ...postParams,
  });
  return data;
};

export const useUpdateReportWriter = () => {
  return useMutation(writerUpdate);
};


export const useReportTeamData = () => {
  const { authAxios } = useFetchContext();

  const getReportTeamData = async (): Promise<any> => {

    const { data } = await authAxios.get(urls.reportWriterAgenda.addReportTeam);
    return data.results;
  }
  return useQuery(["ReportTeamData"], getReportTeamData)
}

export const useEditReportTeamData = (reportTeamId: string) => {
  const { authAxios } = useFetchContext();

  const getEditReportTeamData = async (): Promise<any> => {

    const { data } = await authAxios.get(urls.reportApproversAgenda.edit_reportTeam(reportTeamId));
    return data;
  }
  return useQuery(["ReportTeamData", reportTeamId], getEditReportTeamData, { enabled: !!reportTeamId })
}

export const useReportWritersAndReviewer = () => {
  const { authAxios } = useFetchContext();

  const getWritersAndReviewers = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportWriterAgenda.writers);
    return data;
  };

  return useQuery("writers", getWritersAndReviewers);
};

export const useReportComments = (id: any, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: ["report-comments", id],
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.reportWriterAgenda.notes(id));
      return data;
    },
    enabled: id !== undefined && id !== null,
    ...options,
  });
};

export const moveToApprover = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportWriterAgenda.moveApprover, {
    ...postParams,
  });
  return data;
};


export const useWriterToApprover = () => {
  return useMutation(moveToApprover);
};

export const updateReportTeam = async (params: any): Promise<any> => {

  const { authAxios, id, ...postParams } = params;

  const { data } = await authAxios.put(urls.reportApproversAgenda.edit_reportTeam(id), {
    ...postParams,
  });
  return data;
};


export const useUpdateReportTeam = () => {
  return useMutation(updateReportTeam);
};

export const addReportDate = async (params: any): Promise<any> => {

  const { authAxios, reportId, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportWriterAgenda.addTechWriterDate(reportId), {
    ...postParams,
  });
  return data;
};

export const useAddReportDate = () => {
  return useMutation(addReportDate);
};

export const addDateByPM = async (params: any): Promise<any> => {

  const { authAxios, reportId, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportApproversAgenda.addDateVerifiedByPM(reportId), {
    ...postParams,
  });
  return data;
};

export const useAddDateByPM = () => {
  return useMutation(addDateByPM);
};

export const addDateByApprover = async (params: any): Promise<any> => {

  const { authAxios, reportId, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportApproversAgenda.addDateApprover(reportId), {
    ...postParams,
  });
  return data;
};

export const useAddDateByApprover = () => {
  return useMutation(addDateByApprover);
};


export const addDateDelivered = async (params: any): Promise<any> => {

  const { authAxios, reportId, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportApproversAgenda.addDateDelivered(reportId), {
    ...postParams,
  });
  return data;
};


export const useAddDateDelivered = () => {
  return useMutation(addDateDelivered);
};

export const moveToDelivered = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportApproversAgenda.moveDelivered, {
    ...postParams,
  });
  return data;
};


export const usemoveToDelivered = () => {
  return useMutation(moveToDelivered);
};


export const moveToWriter = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportApproversAgenda.moveWriter, {
    ...postParams,
  });
  return data;
};


export const useMoveToWriter = () => {
  return useMutation(moveToWriter);
};

export const useDeliveredHistory = (enabled = true) => {
  const { authAxios } = useFetchContext();

  const getWriterHistory = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportApproversAgenda.deliveredHistory);
    return data.results;
  };

  return useQuery("delivered_history", getWriterHistory, {
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useReportApprovers = () => {
  const { authAxios } = useFetchContext();

  const getWritersAndApprover = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportWriterAgenda.approvers);
    return data;
  };

  return useQuery("Approvers", getWritersAndApprover);
};

export const useReportType = () => {
  const { authAxios } = useFetchContext();

  const getReportType = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportWriterAgenda.reporttype);
    return data.results;
  };

  return useQuery("reporttype", getReportType);
};

export const addReportTeam = async (params: any): Promise<any> => {

  const { authAxios, ...postParams } = params;

  const { data } = await authAxios.post(urls.reportWriterAgenda.addReportTeam, {
    ...postParams,
  });
  return data;
};


export const useReportTeamInsert = () => {
  return useMutation(addReportTeam);
};

export const useProjectType = () => {
  const { authAxios } = useFetchContext();

  const getProjectType = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.projects.projectType);
    return data.results;
  };
  return useQuery("projecttype", getProjectType);
};

export const useReportTypeUpload = (version: string | null) => {
  const { authAxios } = useFetchContext();

  const ReportTypeUpload = async (): Promise<any> => {
    if (!version) {
      return [];
    }

    const { data } = await authAxios.get(
      urls.reportWriterAgenda.ReportTypeUpload(version),
    );

    return data;
  };

  return useQuery(["ReportTypeUpload", version], ReportTypeUpload, {
    enabled: !!version,
  });
};
